import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatMap, delay, exhaustMap, tap } from 'rxjs';
import { FileManagerEntitaService, UserService } from '../../../api/services';
import { mapResponse } from '@ngrx/operators';
import { LoadingState } from '../models/loader.model';
import { usersActions } from './users.actions';

import { Store } from '@ngrx/store';
import { getErrorMessage, ZetraError } from '../../shared/errors/error-messages.helper';

export const usersLoadEffect = createEffect(
  (actions$ = inject(Actions), usersService = inject(UserService)) => {
    return actions$.pipe(
      ofType(usersActions.load),
      concatMap((props) =>
        usersService.userControllerFind(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => usersActions.done({ data: r.data, state: { value: LoadingState.SUCCESS }, request: props }),
            error: (error: ZetraError) =>
              usersActions.done({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const usersLoadOneEffect = createEffect(
  (actions$ = inject(Actions), usersService = inject(UserService)) => {
    return actions$.pipe(
      ofType(usersActions.loadOne),
      exhaustMap((props) =>
        usersService.userControllerFindOne(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => usersActions.doneOne({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              usersActions.doneOne({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const usersLoadOneMediaEffect = createEffect(
  (actions$ = inject(Actions), entityMediaService = inject(FileManagerEntitaService)) => {
    return actions$.pipe(
      ofType(usersActions.loadOneMedia),
      delay(1000),
      exhaustMap((props) =>
        entityMediaService.entityMediaControllerGetEntityFiles(props).pipe(
          mapResponse({
            next: (r) => usersActions.loadOneMediaDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              usersActions.loadOneMediaDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const usersDeleteOneMediaEffect = createEffect(
  (actions$ = inject(Actions), entityMediaService = inject(FileManagerEntitaService)) => {
    return actions$.pipe(
      ofType(usersActions.deleteOneMedia),
      delay(1000),
      exhaustMap((props) =>
        entityMediaService.entityMediaControllerDetachAndDelete(props).pipe(
          mapResponse({
            next: (r) =>
              usersActions.deleteOneMediaDone({
                entity_id: props.entityId,
                data: r.data,
                state: { value: LoadingState.SUCCESS },
              }),
            error: (error: ZetraError) =>
              usersActions.deleteOneMediaDone({
                entity_id: props.entityId,
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const usersSaveOneEffect = createEffect(
  (actions$ = inject(Actions), usersService = inject(UserService)) => {
    return actions$.pipe(
      ofType(usersActions.saveOne),
      exhaustMap((props) =>
        usersService.userControllerUpdate(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => usersActions.saveOneDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              usersActions.saveOneDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const usersCreateOneEffect = createEffect(
  (actions$ = inject(Actions), usersService = inject(UserService)) => {
    return actions$.pipe(
      ofType(usersActions.createOne),
      exhaustMap((props) =>
        usersService.userControllerCreate(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => usersActions.createOneDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              usersActions.createOneDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const uploadAvatarEffect = createEffect(
  (actions$ = inject(Actions), usersService = inject(UserService)) => {
    return actions$.pipe(
      ofType(usersActions.uploadAvatar),
      exhaustMap((props) =>
        usersService.userControllerUploadPicture(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => usersActions.uploadAvatarDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              usersActions.uploadAvatarFail({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
//sposta un utente nel cestino
export const moveUserToTrashEffect = createEffect(
  (actions$ = inject(Actions), usersService = inject(UserService)) => {
    return actions$.pipe(
      ofType(usersActions.moveToTrash),
      exhaustMap((props) =>
        usersService.userControllerMoveToTrash(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => usersActions.moveToTrashDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              usersActions.moveToTrashDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

//ripristina un utente dal cestino
export const restoreFromTrashEffect = createEffect(
  (actions$ = inject(Actions), usersService = inject(UserService)) => {
    return actions$.pipe(
      ofType(usersActions.restoreFromTrash),
      exhaustMap((props) =>
        usersService.userControllerRestoreFromTrash(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => usersActions.restoreFromTrashDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              usersActions.restoreFromTrashDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

//cancellazione definitiva
export const forceDeleteEffect = createEffect(
  (actions$ = inject(Actions), usersService = inject(UserService)) => {
    return actions$.pipe(
      ofType(usersActions.forceDelete),
      exhaustMap((props) =>
        usersService.userControllerForceDelete(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => usersActions.forceDeleteDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              usersActions.forceDeleteDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

//users in trash
export const usersInTrashLoadEffect = createEffect(
  (actions$ = inject(Actions), usersService = inject(UserService)) => {
    return actions$.pipe(
      ofType(usersActions.trashList),
      concatMap((props) =>
        usersService.userControllerUsersInTrash(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => usersActions.trashListDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              usersActions.trashListDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const usersUpdateCredentialsEffect = createEffect(
  (actions$ = inject(Actions), usersService = inject(UserService)) => {
    return actions$.pipe(
      ofType(usersActions.updateCredentials),
      exhaustMap((props) =>
        usersService.userControllerUpdateCredentials(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) => usersActions.updateCredentialsDone({ data: r.data, state: { value: LoadingState.SUCCESS } }),
            error: (error: ZetraError) =>
              usersActions.updateCredentialsDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const userupdateCredentialsDoneEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(usersActions.updateCredentialsDone),
      tap((action) => {
        if (action.state.value === LoadingState.SUCCESS) {
          store.dispatch(
            usersActions.loadOne({
              id: action.data!.id,
            }),
          );
        }
      }),
    );
  },
  { functional: true, dispatch: false },
);
